import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// =============================================================================
// GridPager — the strip that sits UNDER a grid and says which slice of the data
// is on screen, with the controls to move through it.
//
// Grids no longer load a whole budget version: a sales plan by customer is
// twelve rows per SKU-per-customer, so half a million planning lines is six
// million rows. The server returns a page and this says so.
//
// It lives below the grid rather than above deliberately — "showing 1–100 of
// 3,412" is the answer to a question you ask after looking at the rows, not
// before, and the top of a capture screen belongs to the data.
//
// `total` is null when nothing is filtered (counting every row of an unfiltered
// six-million-row table costs as much as the query and answers nothing useful).
// The pager copes: without a total it still knows the current range and, from
// `hasMore`, whether a Next page exists.
// =============================================================================

const PAGE_SIZES = [50, 100, 200];

const GridPager = ({
  offset = 0,
  limit = 100,
  rowCount = 0,      // rows actually on screen
  total = null,      // exact match count, or null when unfiltered
  hasMore = false,
  loading = false,
  onOffset,
  onLimit,
}) => {
  const first = rowCount === 0 ? 0 : offset + 1;
  const last = offset + rowCount;
  const canPrev = offset > 0;
  const canNext = hasMore;

  const btn = 'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition-colors '
    + 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 '
    + 'hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="shrink-0 flex flex-wrap items-center gap-3 px-3.5 py-2 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-[11px] text-slate-500 dark:text-slate-400">
      <span className={canNext || (total !== null && total > last)
        ? 'font-semibold text-amber-600 dark:text-amber-400' : undefined}>
        {loading ? 'Loading…'
          : rowCount === 0 ? 'No rows'
            : total !== null
              ? `Showing ${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`
              : `Showing ${first.toLocaleString()}–${last.toLocaleString()}${canNext ? '+' : ''}`}
      </span>

      <span className="flex items-center gap-1.5">
        <span>Rows</span>
        <select
          value={limit}
          onChange={(e) => onLimit?.(Number(e.target.value))}
          className="text-[11px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 px-1.5 py-0.5 outline-none focus:ring-2 focus:ring-slate-400"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </span>

      <span className="ml-auto flex items-center gap-1.5">
        <button type="button" className={btn} disabled={!canPrev || loading}
          onClick={() => onOffset?.(Math.max(0, offset - limit))}>
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        <button type="button" className={btn} disabled={!canNext || loading}
          onClick={() => onOffset?.(offset + limit)}>
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </span>

      {/* Only shown when it is actually true, so it stays a warning rather than
          decoration: totals on the grid cover the visible page only. */}
      {(canNext || offset > 0) && (
        <span className="w-full text-[10px] text-slate-400 dark:text-slate-500">
          Totals shown in the grid cover this page only. Filter a column heading to narrow the set.
        </span>
      )}
    </div>
  );
};

export default GridPager;
